/* /professional/dashboard — Authenticated landing for a verified professional.
 *
 * Three sections:
 *   - Profile summary + availability toggle
 *   - Inquiry inbox (assigned by admin or sent directly to the pro)
 *   - Quick links (edit profile fields inline)
 *
 * Uses its own /me, /me/inquiries fetch + cookie. If the session is invalid
 * we redirect to /professional/login. Admin can edit deeper from the admin
 * dashboard (this view is intentionally focused for the professional).
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import {
  Loader2,
  LogOut,
  Inbox,
  RefreshCw,
  Phone,
  MapPin,
  CheckCircle2,
  Clock3,
  Pause,
  Power,
  Edit3,
  Save,
  AlertTriangle,
  ImagePlus,
  X,
  Image as ImageIcon,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import BrandLogo from "@/components/BrandLogo";
import { CATEGORY_BY_KEY, VerifiedBadge, categoryHeadline } from "@/professionals/categories";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INQUIRY_STATUS_META = {
  received: { label: "Inquiry received", tone: "info" },
  assigned: { label: "Assigned to you", tone: "warn" },
  consultation_scheduled: { label: "Consultation scheduled", tone: "info" },
  in_progress: { label: "In progress", tone: "info" },
  completed: { label: "Completed", tone: "ok" },
  closed: { label: "Closed", tone: "off" },
};

const StatusPill = ({ status }) => {
  const meta = INQUIRY_STATUS_META[status] || { label: status, tone: "off" };
  const palette =
    meta.tone === "ok"
      ? "bg-emerald-100 text-emerald-800 border-emerald-300"
      : meta.tone === "warn"
      ? "bg-yellow-100 text-yellow-900 border-yellow-300"
      : meta.tone === "info"
      ? "bg-sky-100 text-sky-900 border-sky-300"
      : "bg-sand-alt text-muted-foreground border-border";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-extrabold uppercase tracking-wider ${palette}`}>
      {meta.label}
    </span>
  );
};

const ProfessionalDashboard = () => {
  const navigate = useNavigate();
  const [pro, setPro] = useState(null);
  const [inquiries, setInquiries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    document.title = "Professional Dashboard · Mera Naksha";
  }, []);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [meRes, inqRes] = await Promise.all([
        axios.get(`${API}/pro/auth/me`),
        axios.get(`${API}/pro/me/inquiries`),
      ]);
      setPro(meRes.data);
      setInquiries(inqRes.data.items || []);
      setError("");
    } catch (e) {
      if (e?.response?.status === 401) {
        navigate("/professional/login?next=" + encodeURIComponent("/professional/dashboard"), { replace: true });
        return;
      }
      setError(e?.response?.data?.detail || "Could not load your dashboard. Please try again.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const handleLogout = async () => {
    try { await axios.post(`${API}/pro/auth/logout`); } catch (e) { console.warn("pro logout failed:", e?.message || e); }
    navigate("/professional/login", { replace: true });
  };

  const toggleAccepting = async () => {
    if (!pro) return;
    const value = !pro.accepting_new_inquiries;
    try {
      const { data } = await axios.patch(`${API}/pro/me`, { accepting_new_inquiries: value });
      setPro(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not update availability.");
    }
  };

  const beginEdit = () => {
    setDraft({
      bio: pro?.bio || "",
      city: pro?.city || "",
      state: pro?.state || "",
      experience_years: pro?.experience_years || 0,
      consultation_fee: pro?.consultation_fee ?? "",
      service_areas: (pro?.service_areas || []).join(", "),
      languages: (pro?.languages || []).join(", "),
    });
    setEditing(true);
    setSaveError("");
  };

  const saveProfile = async () => {
    setSaving(true);
    setSaveError("");
    try {
      const payload = {
        bio: draft.bio.trim(),
        city: draft.city.trim(),
        state: draft.state.trim(),
        experience_years: Number(draft.experience_years) || 0,
        consultation_fee: draft.consultation_fee === "" ? null : Number(draft.consultation_fee),
        service_areas: draft.service_areas.split(/[,\n]/).map((s) => s.trim()).filter(Boolean),
        languages: draft.languages.split(/[,\n]/).map((s) => s.trim()).filter(Boolean),
      };
      const { data } = await axios.patch(`${API}/pro/me`, payload);
      setPro(data);
      setEditing(false);
    } catch (e) {
      setSaveError(e?.response?.data?.detail || "Could not save changes.");
    } finally {
      setSaving(false);
    }
  };

  const updateInquiry = async (inq, status) => {
    try {
      const { data } = await axios.patch(`${API}/pro/me/inquiries/${inq.id}`, { status });
      setInquiries((prev) => prev.map((x) => (x.id === inq.id ? data : x)));
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not update inquiry.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sand grid place-items-center" data-testid="pro-dashboard-loading">
        <div className="inline-flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      </div>
    );
  }

  if (!pro) {
    return (
      <div className="min-h-screen bg-sand grid place-items-center" data-testid="pro-dashboard-error">
        <div className="text-center">
          <AlertTriangle className="h-10 w-10 mx-auto text-red-500" />
          <div className="mt-2 font-bold text-navy">{error || "Could not load your account."}</div>
          <Link to="/professional/login" className="mt-3 inline-block px-4 py-2 rounded-xl bg-navy text-gold font-bold">Sign in again</Link>
        </div>
      </div>
    );
  }

  const meta = CATEGORY_BY_KEY[pro.category];
  const verified = pro.status === "verified";
  const initials = pro.full_name.split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

  return (
    <div className="min-h-screen bg-sand" data-testid="pro-dashboard">
      <header className="sticky top-0 z-30 bg-navy text-white border-b-4 border-gold shadow-md">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/" className="inline-flex items-center bg-white rounded-lg px-2 py-1 shadow-sm">
              <BrandLogo size="sm" asLink={false} withTagline lang="en" testId="pro-dashboard-brand-logo" />
            </Link>
            <div className="leading-tight min-w-0">
              <div className="text-[11px] uppercase tracking-[0.2em] text-gold font-bold truncate">Professional Dashboard</div>
              <div className="text-[15px] font-extrabold tracking-tight truncate">{pro.full_name}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={load}
              disabled={refreshing}
              data-testid="pro-dashboard-refresh"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-bold border border-white/15 disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              type="button"
              onClick={handleLogout}
              data-testid="pro-dashboard-logout"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-red-500/15 hover:bg-red-500/30 text-red-200 hover:text-white text-sm font-bold border border-red-400/30"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-8 space-y-6">
        {/* Profile summary */}
        <section className="rounded-3xl bg-navy-jaali text-white p-5 sm:p-7 relative overflow-hidden" data-testid="pro-dashboard-profile">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-gold-dark via-gold to-gold-dark" />
          <div className="flex items-start gap-4 flex-wrap">
            <div className="h-20 w-20 rounded-2xl bg-white text-navy font-extrabold text-2xl grid place-items-center border-2 border-gold/50 overflow-hidden">
              {pro.profile_photo_url ? <img src={pro.profile_photo_url} alt="" className="h-full w-full object-cover" /> : initials}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight">{pro.full_name}</h1>
                {verified ? <VerifiedBadge /> : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-extrabold uppercase tracking-wider bg-yellow-100 text-yellow-900 border border-yellow-300">
                    <Clock3 className="h-3 w-3" /> Pending verification
                  </span>
                )}
              </div>
              <div
                className="mt-1 inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full text-white"
                style={{ backgroundColor: meta?.accent || "#0b2545" }}
              >
                {categoryHeadline(pro.category)}
              </div>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 text-[12.5px] text-white/85">
                <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-gold" /> {pro.city}, {pro.state}</span>
                <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-gold" /> +91 {pro.phone}</span>
                <span className="inline-flex items-center gap-1.5"><FaWhatsapp className="h-3.5 w-3.5 text-emerald-300" /> {pro.email}</span>
              </div>
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={toggleAccepting}
                  data-testid="pro-dashboard-toggle-availability"
                  className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12.5px] font-extrabold border ${
                    pro.accepting_new_inquiries
                      ? "bg-emerald-500 text-white border-emerald-700"
                      : "bg-white/10 text-white border-white/25"
                  }`}
                >
                  <Power className="h-3.5 w-3.5" />
                  {pro.accepting_new_inquiries ? "Accepting new inquiries" : "Currently paused"}
                </button>
                {!editing && (
                  <button
                    type="button"
                    onClick={beginEdit}
                    data-testid="pro-dashboard-edit"
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12.5px] font-extrabold bg-gold text-navy hover:bg-gold-dark hover:text-white"
                  >
                    <Edit3 className="h-3.5 w-3.5" /> Edit profile
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>

        {editing && (
          <section className="rounded-3xl bg-white border-2 border-gold/40 p-5 sm:p-7" data-testid="pro-dashboard-edit-form">
            <div className="flex items-center justify-between gap-3 mb-3">
              <h2 className="text-lg font-extrabold text-navy">Edit your profile</h2>
              <button type="button" onClick={() => setEditing(false)} className="text-[12.5px] text-muted-foreground font-bold hover:text-navy">Cancel</button>
            </div>
            {saveError && (
              <div className="mb-3 rounded-xl bg-red-50 border-2 border-red-200 px-3 py-2 text-[13px] text-red-800 font-semibold">{saveError}</div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { k: "city", label: "City" },
                { k: "state", label: "State" },
                { k: "experience_years", label: "Years of experience", type: "number" },
                { k: "consultation_fee", label: "Consultation fee (₹)", type: "number" },
                { k: "languages", label: "Languages (comma separated)" },
                { k: "service_areas", label: "Service areas (comma separated)" },
              ].map((f) => (
                <label key={f.k} className="block">
                  <span className="text-[10.5px] uppercase tracking-[0.18em] text-navy/70 font-bold">{f.label}</span>
                  <input
                    type={f.type || "text"}
                    value={draft[f.k] ?? ""}
                    onChange={(e) => setDraft((d) => ({ ...d, [f.k]: e.target.value }))}
                    data-testid={`pro-edit-${f.k}`}
                    className="mt-1.5 w-full h-11 px-3 rounded-xl border-2 border-border bg-white text-[14px] outline-none focus:border-navy"
                  />
                </label>
              ))}
              <label className="block sm:col-span-2">
                <span className="text-[10.5px] uppercase tracking-[0.18em] text-navy/70 font-bold">Bio</span>
                <textarea
                  rows={4}
                  maxLength={2000}
                  value={draft.bio ?? ""}
                  onChange={(e) => setDraft((d) => ({ ...d, bio: e.target.value }))}
                  data-testid="pro-edit-bio"
                  className="mt-1.5 w-full px-3 py-2 rounded-xl border-2 border-border bg-white text-[14px] outline-none focus:border-navy resize-y"
                />
              </label>
            </div>
            <button
              type="button"
              onClick={saveProfile}
              disabled={saving}
              data-testid="pro-edit-save"
              className="mt-4 min-h-[44px] px-5 rounded-xl bg-gold text-navy font-extrabold text-[14px] inline-flex items-center gap-1.5 hover:bg-gold-dark hover:text-white disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save profile
            </button>
          </section>
        )}

        {/* Portfolio gallery — pro can paste image URLs to showcase recent
            work. Upload-to-our-server is intentionally deferred; pasting
            a CDN URL keeps the v1 flow lightweight and prevents abuse. */}
        <PortfolioManager pro={pro} onChange={(urls) => setPro((p) => ({ ...p, portfolio_urls: urls }))} />

        {/* Inquiries */}
        <section data-testid="pro-dashboard-inquiries">
          <div className="flex items-end justify-between gap-3 mb-3">
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.2em] text-navy/70 font-extrabold">Inbox</div>
              <h2 className="text-lg sm:text-xl font-extrabold tracking-tight text-navy">Customer inquiries</h2>
            </div>
            {error && (
              <div className="text-[12.5px] text-red-700 font-semibold">{error}</div>
            )}
          </div>
          {(inquiries || []).length === 0 ? (
            <div className="rounded-3xl bg-white border-2 border-dashed border-border p-10 text-center" data-testid="pro-dashboard-empty">
              <Inbox className="h-10 w-10 mx-auto text-navy/30" />
              <div className="mt-3 font-bold text-navy">No inquiries yet</div>
              <div className="text-[13px] text-muted-foreground mt-1 max-w-md mx-auto">
                Once a customer requests a consultation, you'll see it here. Make
                sure your profile is complete so we can match you accurately.
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {inquiries.map((inq) => (
                <article key={inq.id} className="rounded-3xl bg-white border-2 border-border overflow-hidden shadow-sm" data-testid={`pro-inquiry-${inq.id}`}>
                  <div className="p-4 sm:p-5 space-y-2.5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="text-[15px] font-extrabold text-navy">{inq.name}</h3>
                        <div className="text-[12px] text-muted-foreground inline-flex items-center gap-1.5 mt-0.5">
                          <MapPin className="h-3 w-3" /> {inq.city}{inq.plot_size ? ` · ${inq.plot_size}` : ""}
                        </div>
                      </div>
                      <StatusPill status={inq.status} />
                    </div>
                    <p className="text-[13.5px] text-foreground whitespace-pre-wrap leading-relaxed">{inq.description}</p>
                    <div className="text-[11.5px] text-muted-foreground">
                      Received {new Date(inq.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                    </div>
                    <div className="pt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <a
                        href={`https://wa.me/91${inq.phone}?text=${encodeURIComponent(`Hi ${inq.name}, this is ${pro.full_name} from Mera Naksha. I just received your inquiry — happy to discuss your requirements.`)}`}
                        target="_blank" rel="noopener noreferrer"
                        data-testid={`pro-inquiry-wa-${inq.id}`}
                        className="min-h-[40px] rounded-xl bg-emerald-600 text-white font-extrabold text-[12.5px] inline-flex items-center justify-center gap-1.5 hover:bg-emerald-700"
                      >
                        <FaWhatsapp className="h-3.5 w-3.5" /> WhatsApp customer
                      </a>
                      <select
                        value={inq.status}
                        onChange={(e) => updateInquiry(inq, e.target.value)}
                        data-testid={`pro-inquiry-status-${inq.id}`}
                        className="min-h-[40px] rounded-xl border-2 border-border bg-white text-[12.5px] px-3 font-bold text-navy focus:border-navy outline-none"
                      >
                        <option value="assigned">Assigned</option>
                        <option value="consultation_scheduled">Consultation scheduled</option>
                        <option value="in_progress">In progress</option>
                        <option value="completed">Completed</option>
                        <option value="closed">Closed</option>
                      </select>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        <section className="rounded-2xl bg-white border border-border p-4 flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-navy text-gold grid place-items-center shrink-0">
            <Pause className="h-5 w-5" />
          </div>
          <div className="text-[13px]">
            <div className="font-bold text-navy">Need to take a break?</div>
            <div className="text-muted-foreground">
              Toggle <strong>"Accepting new inquiries"</strong> off and we'll route new requests
              to other verified professionals until you're back. You won't lose existing inquiries.
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ProfessionalDashboard;

// ---------------------- Portfolio manager ----------------------

const PortfolioManager = ({ pro, onChange }) => {
  const [urls, setUrls] = useState(pro?.portfolio_urls || []);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setUrls(pro?.portfolio_urls || []);
  }, [pro?.portfolio_urls]);

  const isLikelyImage = (s) => /^https?:\/\/[^\s]+/i.test(s);

  const add = async (e) => {
    e?.preventDefault();
    setError("");
    const url = draft.trim();
    if (!isLikelyImage(url)) { setError("Paste a public https URL (jpg / png / webp)."); return; }
    if (urls.includes(url)) { setError("That image is already in your gallery."); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/pro/me/portfolio`, { url });
      setUrls(data.portfolio_urls || []);
      onChange?.(data.portfolio_urls || []);
      setDraft("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not add image.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (url) => {
    setBusy(true);
    try {
      const { data } = await axios.delete(`${API}/pro/me/portfolio`, { params: { url } });
      setUrls(data.portfolio_urls || []);
      onChange?.(data.portfolio_urls || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not remove image.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-3xl bg-white border-2 border-border p-5 sm:p-7" data-testid="pro-portfolio-manager">
      <div className="flex items-end justify-between gap-3 mb-3">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.2em] text-navy/70 font-extrabold">Portfolio</div>
          <h2 className="text-lg font-extrabold text-navy">Showcase your work</h2>
          <p className="text-[12.5px] text-muted-foreground">
            Paste public image URLs of recent projects. Up to 24 images.
          </p>
        </div>
        <span className="text-[11.5px] font-bold text-navy tabular-nums">{urls.length}/24</span>
      </div>

      <form onSubmit={add} className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <ImagePlus className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="url"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="https://cdn.example.com/your-project.jpg"
            data-testid="pro-portfolio-add-url"
            className="w-full h-11 pl-9 pr-3 rounded-xl border-2 border-border bg-white text-[14px] outline-none focus:border-navy"
          />
        </div>
        <button
          type="submit"
          disabled={busy || !draft.trim()}
          data-testid="pro-portfolio-add-submit"
          className="min-h-[44px] px-4 rounded-xl bg-gold text-navy font-extrabold text-[13.5px] inline-flex items-center justify-center gap-1.5 hover:bg-gold-dark hover:text-white disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
          Add image
        </button>
      </form>
      {error && (
        <div className="mt-2 text-[12.5px] text-red-700 font-semibold" data-testid="pro-portfolio-error">{error}</div>
      )}

      {urls.length === 0 ? (
        <div className="mt-4 rounded-2xl border-2 border-dashed border-border p-8 text-center text-muted-foreground" data-testid="pro-portfolio-empty">
          <ImageIcon className="h-10 w-10 mx-auto text-navy/30" />
          <div className="mt-2 font-bold text-navy">No portfolio images yet</div>
          <div className="text-[12.5px]">Your public profile is more persuasive with a few real photos of completed work.</div>
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="pro-portfolio-grid">
          {urls.map((url, i) => (
            <div key={url + i} className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-sand-alt border border-border" data-testid={`pro-portfolio-item-${i}`}>
              <img src={url} alt={`Portfolio ${i + 1}`} loading="lazy" className="h-full w-full object-cover" />
              <button
                type="button"
                onClick={() => remove(url)}
                disabled={busy}
                data-testid={`pro-portfolio-remove-${i}`}
                aria-label="Remove image"
                className="absolute top-2 right-2 h-8 w-8 rounded-full bg-black/55 text-white grid place-items-center hover:bg-red-600 disabled:opacity-50"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

